/**
 * Brand & domain configuration for multi-tenant deployment.
 * Supports baiglabs (chat.baiglabs.com) and interviewflow.studio.
 */

const isLocalhost = () =>
  typeof window !== 'undefined'
  && window.location
  && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

/**
 * Detects brand from VITE_APP_DOMAIN (build-time) or window.location.hostname (runtime).
 * @returns {'interviewflow' | 'baiglabs'}
 */
export const getBrand = () => {
  const domain = import.meta.env.VITE_APP_DOMAIN || (typeof window !== 'undefined' ? window.location?.hostname : '');
  return domain && String(domain).includes('interviewflow') ? 'interviewflow' : 'baiglabs';
};

/**
 * When true, the browser calls Node `/api/v1/langgraph-proxy/...` which forwards to PYTHON_BACKEND_URL
 * (single auth surface, hides Python from the public internet).
 * Set `VITE_LANGGRAPH_THROUGH_NODE=1` in the client env build.
 */
export const isLangGraphThroughNode = () => {
  const v = import.meta.env.VITE_LANGGRAPH_THROUGH_NODE;
  return v === '1' || String(v).toLowerCase() === 'true';
};

/**
 * Returns the main API base URL.
 */
export const getApiBaseUrl = () => {
  if (import.meta.env.MODE === 'development' || isLocalhost()) {
    return import.meta.env.VITE_API_URL || 'http://localhost:5001/api/v1';
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api/v1`;
  }
  return import.meta.env.VITE_API_URL || 'https://chat.baiglabs.com/api/v1';
};

/**
 * Returns the LangGraph backend base URL.
 * - With `VITE_LANGGRAPH_THROUGH_NODE`: same origin + `/api/v1/langgraph-proxy` (Node reverse proxy).
 * - Dev default: http://localhost:8001
 * - Prod default: {origin}/langgraph (e.g. reverse proxy / path-based gateway)
 */
export const getLangGraphBaseUrl = () => {
  if (isLangGraphThroughNode()) {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/api/v1/langgraph-proxy`;
    }
    const apiBase = getApiBaseUrl();
    const origin = apiBase.replace(/\/api\/v1\/?$/, '');
    return `${origin}/api/v1/langgraph-proxy`;
  }
  if (import.meta.env.MODE === 'development' || isLocalhost()) {
    return 'http://localhost:8001';
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/langgraph`;
  }
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const base = apiUrl.replace(/\/api\/v1\/?$/, '') || 'https://chat.baiglabs.com';
  return `${base}/langgraph`;
};

/**
 * Base URL for V2 REST APIs (flows/apps definitions). Same host as v1; only path differs.
 */
export const getApiV2BaseUrl = () => {
  if (import.meta.env.MODE === 'development' || isLocalhost()) {
    // Browser dev server: same-origin + Vite proxy → Node (session + Bearer both work).
    if (
      typeof window !== 'undefined'
      && import.meta.env.MODE === 'development'
      && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ) {
      return `${window.location.origin}/api/v2`;
    }
    const v1 = import.meta.env.VITE_API_URL || 'http://localhost:5001/api/v1';
    return v1.replace(/\/api\/v1\/?$/, '/api/v2');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api/v2`;
  }
  const v1 = import.meta.env.VITE_API_URL || 'https://chat.baiglabs.com/api/v1';
  return v1.replace(/\/api\/v1\/?$/, '/api/v2');
};
